import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { notification } from 'antd';

import NoSiteAccessUsersContext from '@totem/components/services/remoteAccess/v2/noSiteAccess/NoSiteAccessUsersContext';
import {
  QRUserRole,
  StatusFilter,
} from '@totem/components/services/remoteAccess/v2/users/types';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { AUTH0_USERS_ROLES_NO_SITE_ACCESS_ENDPOINT } from '@totem/utilities/endpoints';

type Props = {
  children?: ReactNode;
};

const NoSiteAccessUsersContainer = ({ children }: Props) => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const organizationId = userProfile.organization?.id;
  const [loading, setLoading] = useState<boolean>(true);
  const [allRows, setAllRows] = useState<QRUserRole[]>([]);
  const [refreshData, setRefreshData] = useState<boolean>(true);
  const [emailSearch, setEmailSearch] = useState<string>('');
  const [nameSearch, setNameSearch] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);

  useEffect(() => {
    setEmailSearch('');

    setNameSearch('');

    setStatusFilter('all');

    setSelectedUserIds([]);

    setRefreshData(true);
  }, [organizationId]);

  useEffect(() => {
    if (!refreshData) {
      return;
    }

    setRefreshData(false);

    setLoading(true);

    fetch(AUTH0_USERS_ROLES_NO_SITE_ACCESS_ENDPOINT, {
      method: 'GET',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
    })
      .then((res) => res.json())
      .then((result: QRUserRole[]) => {
        setAllRows(result ?? []);
      })
      .catch(() => {
        notification.error({
          message: 'Failed to load no-site-access users.',
        });
      })
      .finally(() => setLoading(false));
  }, [refreshData, getApiToken]);

  const onRefresh = useCallback(() => {
    setRefreshData(true);
  }, []);

  const rows = useMemo(() => {
    const emailTerm = emailSearch.trim().toLowerCase();
    const nameTerm = nameSearch.trim().toLowerCase();

    return allRows.filter((row) => {
      if (statusFilter !== 'all' && row.status !== statusFilter) {
        return false;
      }
      if (emailTerm && !row.user.email?.toLowerCase().includes(emailTerm)) {
        return false;
      }
      if (nameTerm && !row.user.name?.toLowerCase().includes(nameTerm)) {
        return false;
      }
      return true;
    });
  }, [allRows, emailSearch, nameSearch, statusFilter]);

  return (
    <NoSiteAccessUsersContext.Provider
      value={{
        loading,
        rows,
        emailSearch,
        setEmailSearch,
        nameSearch,
        setNameSearch,
        statusFilter,
        setStatusFilter,
        selectedUserIds,
        setSelectedUserIds,
        onRefresh,
      }}
    >
      {children}
    </NoSiteAccessUsersContext.Provider>
  );
};

export default NoSiteAccessUsersContainer;
